/**
 * Structured Logger for React Frontend
 * Provides consistent, structured logging that mirrors the backend logging patterns.
 *
 * Features:
 * - Structured key-value logging
 * - Log levels (debug, info, warn, error)
 * - Component/module context
 * - Development vs production mode
 * - Timestamp formatting
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  [key: string]: unknown
}

// Log level priorities for filtering
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

// Configuration
const config = {
  // In production, only show warnings and errors
  minLevel: (import.meta.env.DEV ? 'debug' : 'warn') as LogLevel,
  // Enable structured output format (matches backend style)
  structured: true,
}

/**
 * Format log entry in structured format similar to backend slog
 * Example: time=2024-01-24T18:25:32 level=INFO component=SSE msg="client connected" client_id=1
 */
function formatStructured(level: LogLevel, component: string, message: string, context: LogContext): string {
  const timestamp = new Date().toISOString()
  const parts = [`time=${timestamp}`, `level=${level.toUpperCase()}`, `component=${component}`, `msg="${message}"`]

  // Add context key-value pairs
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) continue
    const formatted = typeof value === 'string' ? `"${value}"` : JSON.stringify(value)
    parts.push(`${key}=${formatted}`)
  }

  return parts.join(' ')
}

/**
 * Create a logger instance for a specific component
 * @param component - Component or module name (e.g., 'SSE', 'API', 'Devices')
 */
export function createLogger(component: string) {
  const shouldLog = (level: LogLevel): boolean => {
    return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel]
  }

  const log = (level: LogLevel, message: string, context: LogContext = {}) => {
    if (!shouldLog(level)) return

    const consoleFn = level === 'error' ? console.error
      : level === 'warn' ? console.warn
      : level === 'debug' ? console.debug
      : console.info

    if (config.structured) {
      consoleFn(formatStructured(level, component, message, context))
    } else {
      // Fallback to simple format
      consoleFn(`[${component}] ${message}`, context)
    }
  }

  return {
    debug: (message: string, context?: LogContext) => log('debug', message, context || {}),
    info: (message: string, context?: LogContext) => log('info', message, context || {}),
    warn: (message: string, context?: LogContext) => log('warn', message, context || {}),
    error: (message: string, context?: LogContext) => log('error', message, context || {}),
  }
}

// Pre-configured loggers for common components
export const logger = {
  api: createLogger('API'),
  sse: createLogger('SSE'),
  auth: createLogger('Auth'),
  devices: createLogger('Devices'),
  users: createLogger('Users'),
  visitors: createLogger('Visitors'),
  firmware: createLogger('Firmware'),
  audit: createLogger('Audit'),
  events: createLogger('Events'),
  admins: createLogger('Admins'),
  doorGroups: createLogger('DoorGroups'),
  ui: createLogger('UI'),
}

// Default export for quick access
export default logger
